import React, { useState, useEffect, useRef } from 'react'
import { useLocation, useOutletContext } from 'react-router'

import { useGetHomePostsWithTokenQuery } from '../../services/apiSlice'
import { useAppDispatch, useAppSelector } from '../../hooks/redux'
import { setPrevLocation, setPagination } from '../../services/appSlice'
import CustomCreatePostWrapper from '../../components/custom/wrapper/CustomCreatePostWrapper'
import { groupsShort, groupsLong } from '../../utils/dummyData/groups'
import HomeHeaderSimple from '../../components/home/HomeHeaderSimple'
import PostCard from '../../components/post/PostCard'
import { CurrentUser } from '../../services/schema/User'
import Filler from '../../components/utils/Filler'
import Spinner from '../../components/loading/Spinner'

const Home: React.FC = () => {
  const homeRef = useRef<HTMLDivElement>(null)
  const location = useLocation()
  const dispatch = useAppDispatch()
  const user = useOutletContext<CurrentUser>()
  const pagination = useAppSelector((state) => state.app.pagination)
  const prevScrollLoc = useAppSelector(
    (state) => state.app.prevLocation.home.scroll,
  )
  const [selectedCommunity, setSelectedCommunity] = useState(() => {
    if (user.username === 'usertestingaccount') {
      return groupsLong[0]
    }

    return groupsShort[0]
  })
  const [mountReady, setMountReady] = useState(false)

  const { data, isLoading, isSuccess, isError } = useGetHomePostsWithTokenQuery(
    {
      page: pagination.homePosts.page,
    },
  )

  useEffect(() => {
    if (!homeRef.current) return

    // This is for toggling mountReady, which is a checkpoint for homePosts that needs to complete before it mounts. (since posts is cached, it gets rendered first before mobile screen size css checks takes effect on the parent container - therefore postcards will initially overflow their parent container, producing a glitchy bugged effect in the frontend)
    const observer = new ResizeObserver(([entry]) => {
      if (entry.contentRect.width > 0) {
        setMountReady(true)
      }
    })

    observer.observe(homeRef.current)

    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    // also need to check if !isFetching because this means the app still has its cached data to populate the home component, therefore we will be able to land back to the previous scroll location no matter how far that is. If we do not have cached data, user should land at the top of home again
    if (mountReady) {
      if (prevScrollLoc !== null) {
        window.scrollTo(0, parseInt(prevScrollLoc as string, 10))
        dispatch(setPrevLocation({ loc: 'home', scroll: null, url: null }))
        // dispatch(setPagination({ loc: 'user', reset: true }))
      }
      // else will not run when more posts is successfully fetched because this useEffect will only run during initial mount and location change
      else {
        window.scrollTo(0, 0)
        dispatch(setPrevLocation({ loc: 'home', scroll: null, url: null }))
        dispatch(setPagination({ loc: 'user', reset: true }))
      }
    }
  }, [mountReady, location])

  const homePosts =
    isSuccess &&
    data &&
    data.posts.map((post) => {
      return (
        <PostCard
          key={post.postId}
          id={post.postId}
          username={post.username}
          prev={'home'}
          canBeDeleted={user.username === post.username}
        />
      )
    })

  const noPostsDisplay = (
    <h1 className="text-blue flex h-full w-full items-center justify-center font-bold">
      No posts to show
    </h1>
  )

  const errorDisplay = (
    <article className="border-theme hover-sub h-[80px] w-full border-r border-l">
      <h1 className="text-blue flex h-full w-full items-center justify-center font-bold">
        Something went wrong, unable to fetch more posts for now...
      </h1>
    </article>
  )

  const newerPostsRefetch = (
    <article className="border-theme hover-sub h-[60px] w-full border-r border-b border-l">
      <button
        type="button"
        onClick={() => {
          window.scrollTo(0, 0)
          dispatch(setPagination({ loc: 'home', reset: true }))
        }}
        className="text-blue h-full w-full font-bold"
      >
        Load latest posts?
      </button>
    </article>
  )

  return (
    <div
      className="home-container border-theme relative flex h-full min-h-dvh max-w-[600px] min-w-0 grow flex-col items-stretch border-r border-b border-l pb-[60px] md:mr-0"
      ref={homeRef}
    >
      <HomeHeaderSimple />
      <Filler />
      <div className="border-theme border-r border-b border-l">
        <CustomCreatePostWrapper profilePicture={user.profilePicture} />
      </div>
      {pagination.homePosts.page > 1 && newerPostsRefetch}
      {!mountReady ||
        (isLoading && (
          <div className="flex h-[100px] w-full items-center justify-center">
            <Spinner />
          </div>
        ))}

      {mountReady && data && isSuccess && homePosts}
      {!isError && !isLoading && mountReady ? (
        <article className="border-theme hover-sub h-[80px] w-full">
          {data?.nextPage ? (
            <button
              type="button"
              onClick={() => {
                window.scrollTo(0, 0)
                dispatch(setPagination({ loc: 'home' }))
              }}
              className="text-blue h-full w-full font-bold"
            >
              Load older posts?
            </button>
          ) : (
            noPostsDisplay
          )}
        </article>
      ) : (
        errorDisplay
      )}
    </div>
  )
}

export default Home
